class HomeHero extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-home-hero-track]');
    this.slides = Array.from(this.querySelectorAll('[data-home-hero-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-home-hero-dot]'));

    if (!this.track || this.slides.length === 0) return;

    this.currentIndex = 0;
    this.touchStartX = null;
    this.ignoreClick = false;
    this.handleClick = this.onClick.bind(this);
    this.handleKeydown = this.onKeydown.bind(this);
    this.handleTouchStart = this.onTouchStart.bind(this);
    this.handleTouchEnd = this.onTouchEnd.bind(this);
    this.handleBlockSelect = this.onBlockSelect.bind(this);

    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);
    this.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.addEventListener('touchend', this.handleTouchEnd, { passive: true });
    document.addEventListener('shopify:block:select', this.handleBlockSelect);

    this.select(0, false);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
    this.removeEventListener('touchstart', this.handleTouchStart);
    this.removeEventListener('touchend', this.handleTouchEnd);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    window.clearTimeout(this.clickResetTimer);
  }

  onClick(event) {
    if (this.ignoreClick) {
      event.preventDefault();
      this.ignoreClick = false;
      return;
    }

    const target = event.target instanceof Element ? event.target : null;
    const control = target?.closest('[data-home-hero-action], [data-home-hero-dot]');
    if (!control || !this.contains(control)) return;

    if (control.hasAttribute('data-home-hero-dot')) {
      this.select(Number(control.dataset.index));
      return;
    }

    const offset = control.dataset.homeHeroAction === 'next' ? 1 : -1;
    this.select(this.currentIndex + offset);
  }

  onKeydown(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    this.select(this.currentIndex + (event.key === 'ArrowRight' ? 1 : -1));
  }

  onTouchStart(event) {
    this.touchStartX = event.changedTouches[0]?.clientX ?? null;
  }

  onTouchEnd(event) {
    if (this.touchStartX === null) return;

    const touchEndX = event.changedTouches[0]?.clientX ?? this.touchStartX;
    const distance = touchEndX - this.touchStartX;
    this.touchStartX = null;

    if (Math.abs(distance) < 45) return;
    this.ignoreClick = true;
    window.clearTimeout(this.clickResetTimer);
    this.clickResetTimer = window.setTimeout(() => {
      this.ignoreClick = false;
    }, 350);
    this.select(this.currentIndex + (distance < 0 ? 1 : -1));
  }

  onBlockSelect(event) {
    const target = event.target instanceof Element ? event.target : null;
    const slide = target?.closest('[data-home-hero-slide]');
    if (!slide || !this.contains(slide)) return;

    this.select(Number(slide.dataset.index), false);
  }

  select(requestedIndex, animate = true) {
    const count = this.slides.length;
    this.currentIndex = (requestedIndex + count) % count;

    if (!animate) this.setAttribute('data-no-transition', '');
    this.style.setProperty('--home-hero-offset', `${this.currentIndex * -100}%`);

    this.slides.forEach((slide, index) => {
      const isActive = index === this.currentIndex;
      slide.setAttribute('aria-hidden', String(!isActive));
      slide.toggleAttribute('inert', !isActive);
    });

    this.dots.forEach((dot, index) => {
      const isActive = index === this.currentIndex;
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    if (!animate) {
      requestAnimationFrame(() => requestAnimationFrame(() => this.removeAttribute('data-no-transition')));
    }
  }
}

if (!customElements.get('home-hero')) {
  customElements.define('home-hero', HomeHero);
}
